<template>
  <div>
    <paginated-table
      id="admin-loans-table"
      :columns="columns"
      :extra-filters="extraFilters"
      :load-relations="[
        'loanable',
        'loanable.merged_user_roles',
        'loanable.active_incidents',
        'community',
      ]"
      endpoint="loans"
      :fetch-params="{ for: 'admin' }"
      :label="$tc('loans.model_name', 2)"
      sync-filters-with-url
      :show-action-column="false"
      row-link
    >
      <template #cell(status)="{ item }">
        <loan-status :item="item" small></loan-status>
      </template>
    </paginated-table>
  </div>
</template>

<script>
import LoanStatus from "@/components/Loan/Status/LoanStatusBadge.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column, Filter } from "@/components/shared/PaginatedTableColumns";
import { getCommunityAdminFilter } from "@/helpers/permissions/communities";
import { loanBorrowerLink, loanCommunityLink, loanLoanableLink, userLink } from "@/helpers/links";
import { capitalize } from "@/helpers/filters";
import { canSeeDeletedUser } from "@/helpers/permissions/users";
import dayjs from "dayjs";

export default {
  name: "AdminLoans",
  components: {
    PaginatedTable,
    LoanStatus,
  },
  data() {
    return {
      extraFilters: [
        new Filter(
          "incidents.status",
          this.$t("views.admin.loans.filters.incidents.title"),
          "select",
          [
            { value: null, label: this.$t("views.admin.loans.filters.incidents.all") },
            {
              value: "in_process",
              label: this.$t("views.admin.loans.filters.incidents.in_process"),
            },
            { value: "completed", label: this.$t("views.admin.loans.filters.incidents.completed") },
            { value: "canceled", label: this.$t("views.admin.loans.filters.incidents.canceled") },
          ]
        ),
        new Filter(
          "extension_requested",
          this.$t("views.admin.loans.filters.extension.title"),
          "select",
          [
            { value: null, label: this.$t("views.admin.loans.filters.extension.all") },
            { value: true, label: this.$t("views.admin.loans.filters.extension.in_progress") },
            { value: false, label: this.$t("views.admin.loans.filters.extension.none") },
          ]
        ),
      ],
    };
  },
  computed: {
    columns() {
      const user = this.$store.state.user;
      return [
        new Column("id", this.$t("lists.id"), "id"),
        new Column("departure_at", this.$t("views.admin.loans.columns.departure"), "date", {
          formatter: (value, key, item) => {
            return dayjs.atTz(value, item.loanable.timezone).format("D MMMM YYYY HH:mm");
          },
        }),
        new Column("actual_return_at", this.$t("views.admin.loans.columns.return"), "date", {
          showByDefault: false,
          formatter: (value, key, item) =>
            dayjs.atTz(value, item.loanable.timezone).format("D MMMM YYYY HH:mm"),
        }),
        Column.withRelation(
          "borrower_user.full_name",
          capitalize(this.$t("loans.fields.borrower")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: {
              ...getCommunityAdminFilter(user, "read"),
              with_deleted: canSeeDeletedUser(user),
            },
          },
          "borrower_user.id",
          {
            urlFct: loanBorrowerLink,
          }
        ),
        Column.withRelation(
          "loanable.owner_user.full_name",
          capitalize(this.$t("loanables.fields.owner")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: {
              ...getCommunityAdminFilter(user, "read"),
              with_deleted: canSeeDeletedUser(user),
            },
          },
          "loanable.owner_user_id",
          {
            urlFct: (loan) => userLink(loan.loanable.owner_user),
            sortable: false,
          }
        ),
        Column.withRelation(
          "community.name",
          capitalize(this.$t("communities.fields.community.name")),
          {
            relation: "communities",
            label: "name",
            field: "id",
            params: {
              ...getCommunityAdminFilter(user, "read", "id"),
            },
          },
          "community.id",
          {
            urlFct: loanCommunityLink,
          }
        ),
        Column.withRelation(
          "loanable.name",
          capitalize(this.$t("loans.fields.loanable")),
          {
            relation: "loanables",
            label: "name",
            field: "id",
            params: { for: "admin", with_deleted: true },
          },
          "loanable.id",
          {
            urlFct: loanLoanableLink,
          }
        ),
        Column.withSelect("status", capitalize(this.$t("loans.fields.status")), [
          { value: null, label: capitalize(this.$t("loans.statuses.null.all")) },
          { value: "requested", label: capitalize(this.$t("loans.statuses.requested.label")) },
          { value: "waiting", label: capitalize(this.$t("loans.statuses.waiting.label")) },
          { value: "accepted", label: capitalize(this.$t("loans.statuses.accepted.label")) },
          { value: "confirmed", label: capitalize(this.$t("loans.statuses.confirmed.label")) },
          { value: "ongoing", label: capitalize(this.$t("loans.statuses.ongoing.label")) },
          { value: "ended", label: capitalize(this.$t("loans.statuses.ended.label")) },
          { value: "validated", label: capitalize(this.$t("loans.statuses.validated.label")) },
          { value: "blocked", label: capitalize(this.$t("loans.statuses.blocked.label")) },
          { value: "completed", label: capitalize(this.$t("loans.statuses.completed.label")) },
          { value: "rejected", label: capitalize(this.$t("loans.statuses.rejected.label")) },
          { value: "canceled", label: capitalize(this.$t("loans.statuses.canceled.label")) },
        ]),
        new Column(
          "duration_in_minutes",
          capitalize(this.$t("loans.fields.duration_in_minutes")),
          "number",
          {
            showByDefault: false,
          }
        ),
        new Column("calendar_days", capitalize(this.$t("loans.fields.calendar_days")), "number", {
          showByDefault: false,
        }),
        Column.withSelect(
          "loanable.type",
          capitalize(this.$t("loanables.fields.type")),
          [
            { value: null, label: capitalize(this.$t("loanables.fields.types.all")) },
            { value: "car", label: capitalize(this.$t("loanables.fields.types.car")) },
            { value: "trailer", label: capitalize(this.$t("loanables.fields.types.trailer")) },
            { value: "bike", label: capitalize(this.$t("loanables.fields.types.bike")) },
            {
              value: "car_trailer",
              label: capitalize(this.$t("loanables.fields.types.car_trailer")),
            },
          ],
          { showByDefault: false }
        ),
        Column.withSelect(
          "is_self_service",
          capitalize(this.$t("loans.self_service.is_self_service")),
          [
            { value: null, label: capitalize(this.$t("loans.self_service.all")) },
            { value: true, label: capitalize(this.$t("loans.self_service.is_self_service")) },
            { value: false, label: capitalize(this.$t("loans.self_service.on_demand")) },
          ],
          { showByDefault: false }
        ),
        Column.withSelect(
          "alternative_to",
          capitalize(this.$t("views.admin.loans.columns.transport_mode")),
          [
            { value: null, label: this.$t("views.admin.loans.columns.transport_modes.all") },
            {
              value: "delivery",
              label: this.$t("views.admin.loans.columns.transport_modes.delivery"),
              variant: "success",
            },
            {
              value: "car",
              label: this.$t("views.admin.loans.columns.transport_modes.car"),
              variant: "success",
            },
            {
              value: "public_transit",
              label: this.$t("views.admin.loans.columns.transport_modes.public_transit"),
              variant: "success",
            },
            {
              value: "bike",
              label: this.$t("views.admin.loans.columns.transport_modes.bike"),
              variant: "primary",
            },
            {
              value: "walking",
              label: this.$t("views.admin.loans.columns.transport_modes.walking"),
              variant: "warning",
            },
            {
              value: "other",
              label: this.$t("views.admin.loans.columns.transport_modes.other"),
              variant: "secondary",
            },
          ],
          { showByDefault: false }
        ),
      ];
    },
  },
};
</script>

<style lang="scss"></style>
